import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Select, DatePicker, Row, Col, Upload, Card, Switch, Avatar, Tag, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import api, { imagepath } from '../api';
import { useNavigate } from 'react-router-dom';
import './Employees.css';

const { Option } = Select;

const colors = ['#1890ff', '#f5222d', '#faad14', '#52c41a', '#722ed1', '#13c2c2', '#eb2f96', '#b37feb'];

function getInitials(name) {
  if (!name) return 'EM';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getColor(name) {
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [search, filterDept, filterStatus]);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const navigate = useNavigate();

  const fetchEmployees = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const res = await api.get('/employees', { headers: { Authorization: `Bearer ${token}` } });
    setLoading(false);
    if (res.success) {
      setEmployees(res.data);
    } else {
      message.error(res.message || 'Failed to fetch employees');
    }
  };

  const fetchDepartments = async () => {
    const token = localStorage.getItem('token');
    const res = await api.get('/departments', { headers: { Authorization: `Bearer ${token}` } });
    if (res.success) {
      setDepartments(res.data);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  const openModal = (record = null) => {
    setEditing(record);
    setModalVisible(true);
    if (record) {
      form.setFieldsValue({
        ...record,
        dob: record.dob ? record.dob : null,
        joining_date: record.joining_date ? record.joining_date : null,
        department_id: record.department_id || (record.department ? record.department.id : null),
      });
      setImagePreview(record.profile_image ? (`${imagepath}${record.profile_image}`) : null);
    } else {
      form.resetFields();
      setImagePreview(null);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') formData.append(key, value);
      });
      if (values.profile_image && values.profile_image.file) {
        formData.set('profile_image', values.profile_image.file.originFileObj);
      }
      let res;
      if (editing) {
        res = await api.post(`/employees/${editing.id}?_method=PUT`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post('/employees', formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      }
      if (res.success) {
        message.success(editing ? 'Employee updated' : 'Employee created');
        setModalVisible(false);
        fetchEmployees();
      } else {
        message.error(res.message || 'Operation failed');
      }
    } catch (err) {
      // Validation error
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    const res = await api.delete(`/employees/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.success) {
      message.success('Employee deleted');
      fetchEmployees();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  // Filter employees client-side
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      (emp.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (emp.email || '').toLowerCase().includes(search.toLowerCase());
    const matchesDept = filterDept ? emp.department_id === filterDept : true;
    const matchesStatus = filterStatus ? emp.status === filterStatus : true;
    return matchesSearch && matchesDept && matchesStatus;
  });

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
    },
    { title: 'Emp Code', dataIndex: 'emp_code', key: 'emp_code', width: 100 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Department', dataIndex: ['department', 'name'], key: 'department', render: (_, record) => record.department ? record.department.name : '' },
    {
      title: 'Salary',
      key: 'salary',
      render: (_, record) => {
        if (record.salary_type === 'monthly') {
          return `AED ${record.base_salary} (Monthly)`;
        }
        if (record.salary_type === 'hourly') {
          return `AED ${record.hourly_rate} (Hourly)`;
        }
        return 'N/A';
      }
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
    { title: 'Country', dataIndex: 'country', key: 'country', width: 120 },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <>
          <Button size="small" icon={<EditOutlined />} style={{ marginRight: 8 }} onClick={() => navigate(`/employees/${record.id}/edit`)} />
          <Button size="small" icon={<EyeOutlined />} style={{ marginRight: 8 }} onClick={() => navigate(`/employees/${record.id}/view`)} />
          <Popconfirm title="Are you sure to delete this employee?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>
            Showing {filteredEmployees.length} of {employees.length} employees
            {filterDept && departments.length > 0 && (
              (() => {
                const dept = departments.find(d => d.id === filterDept);
                return dept ? ` in Department: ${dept.name}` : '';
              })()
            )}
          </h2>
          <div style={{ color: '#666', fontSize: 14, marginTop: 4 }}>


          </div>
        </div>
        <div>
          <Switch
            checkedChildren="Card"
            unCheckedChildren="Table"
            checked={viewMode === 'card'}
            onChange={checked => setViewMode(checked ? 'card' : 'table')}
            style={{ marginRight: 16 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/employees/add')}>Add Employee</Button>
        </div>
      </div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Input
            placeholder="Search by name or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={8}>
          <Select
            allowClear
            placeholder="Filter by department"
            style={{ width: '100%' }}
            value={filterDept}
            onChange={setFilterDept}
          >
            {departments.map(dep => <Option key={dep.id} value={dep.id}>{dep.name}</Option>)}
          </Select>
        </Col>
        <Col span={8}>
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: '100%' }}
            value={filterStatus}
            onChange={setFilterStatus}
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </Col>
      </Row>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        viewMode === 'card' ? (
          <Row gutter={[16, 16]}>
            {filteredEmployees.map(emp => {
              const initials = getInitials(emp.name);
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={emp.id} style={{ display: 'flex' }}>
                  <div
                    onClick={() => navigate(`/employees/${emp.id}/view`)}
                    style={{ width: '100%', cursor: 'pointer', borderRadius: 12, transition: 'box-shadow 0.2s', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'relative' }}
                    onMouseEnter={() => setHoveredCardId(emp.id)}
                    onMouseLeave={() => setHoveredCardId(null)}
                  >
                    <Card
                      className="employee-card"
                      style={{ borderRadius: 12, height: '100%', width: '100%', boxShadow: 'none', transition: 'box-shadow 0.3s, transform 0.3s', position: 'relative' }}
                      bodyStyle={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative' }}
                    >
                      {hoveredCardId === emp.id && (
                        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', gap: 4 }}>
                          <Button size="small" icon={<EditOutlined />} style={{ marginRight: 0 }} onClick={e => { e.stopPropagation(); navigate(`/employees/${emp.id}/edit`); }} />
                          <Button size="small" icon={<EyeOutlined />} style={{ marginRight: 0 }} onClick={e => { e.stopPropagation(); navigate(`/employees/${emp.id}/view`); }} />
                          <Popconfirm title="Are you sure to delete this employee?" onConfirm={e => { e.stopPropagation(); handleDelete(emp.id); }} okText="Yes" cancelText="No">
                            <Button size="small" icon={<DeleteOutlined />} danger onClick={e => e.stopPropagation()} />
                          </Popconfirm>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <Avatar
                          size={56}
                          src={emp.profile_image ? `${imagepath}${emp.profile_image}` : null}
                          style={{
                            backgroundColor: emp.profile_image ? 'transparent' : getColor(emp.name),
                            fontWeight: 600,
                            fontSize: 22,
                            marginRight: 18,
                          }}
                        >
                          {!emp.profile_image && initials}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 18 }}>{emp.name}</div>
                          <div style={{ color: '#888', fontSize: 15 }}>{emp.email}</div>
                          <div style={{ color: '#888', fontSize: 15 }}>{emp.phone}</div>
                        </div>
                      </div>
                      {emp.department && (
                        <div style={{ fontWeight: 700, color: '#1890ff', fontSize: 15, margin: '8px 0 12px 0' }}>
                          {emp.department.name}
                        </div>
                      )}
                    </Card>
                  </div>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredEmployees}
            loading={loading}
            bordered
            scroll={{ x: true }}
            pagination={pagination}
            onChange={(p) => setPagination(p)}
          />
        )
      )}
    </div>
  );
};

export default Employees; 